import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Building, ChevronDown } from 'lucide-react';
import { useSession } from '@shared/session/store';
import { Icon, Menu, MenuContent, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuTrigger, Spinner, describeError, toast } from '@shared/ui';
import { homePath, switchOrganization } from '../model/session';

/** Active-organization picker (memberships from /users/me). Switching issues a new access token. */
export function OrgSwitcher() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const memberships = useSession((s) => s.memberships);
  const activeId = useSession((s) => s.activeOrganizationId);
  const [pending, setPending] = useState(false);
  const active = memberships.filter((m) => m.status === 'active');
  const current = active.find((m) => m.organization_id === activeId);

  if (active.length === 0) return null;

  const label = current?.organization_name ?? t('org.choose');

  if (active.length === 1) {
    return (
      <span className="hidden min-w-0 items-center gap-2 text-sm text-secondary md:inline-flex">
        <Icon icon={Building} size={18} />
        <bdi className="max-w-[14rem] truncate">{label}</bdi>
      </span>
    );
  }

  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          aria-label={t('org.switch')}
          className="inline-flex min-h-touch-min min-w-0 items-center gap-2 rounded-md px-2 text-sm text-secondary hover:bg-surface-tint md:min-h-control-md"
        >
          {pending ? <Spinner size={18} /> : <Icon icon={Building} size={18} />}
          <bdi className="max-w-[10rem] truncate md:max-w-[14rem]">{label}</bdi>
          <Icon icon={ChevronDown} size={16} />
        </button>
      </MenuTrigger>
      <MenuContent align="start" label={t('org.switch')}>
        <MenuLabel>{t('org.label')}</MenuLabel>
        <MenuRadioGroup
          value={activeId ?? ''}
          onValueChange={async (value) => {
            if (!value || value === activeId) return;
            setPending(true);
            try {
              await switchOrganization(value);
              queryClient.clear(); // every cached list is org-scoped
              await navigate({ to: homePath() });
            } catch (error) {
              toast({ tone: 'danger', title: describeError(error, t).title });
            } finally {
              setPending(false);
            }
          }}
        >
          {active.map((m) => (
            <MenuRadioItem key={m.organization_id} value={m.organization_id}>
              <bdi>{m.organization_name}</bdi>
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
